'use client';

import React, { useMemo, useState } from 'react';
import Link from 'next/link';
import { FaArrowRight } from 'react-icons/fa';
import { blogPosts, type BlogPostMeta } from './posts';

/**
 * Blog index — July 2026 editorial pass.
 *
 * Was: a card list where every post got an icon chip, gradient background,
 * shadow, avatar byline, and a "Read More" button. At 100+ posts that
 * renders as an endless wall of identical boxes.
 *
 * Now: a journal-style ruled index (topic label | title + one-line excerpt
 * | date + read time), with a quiet text-link topic filter at the top. The
 * byline is dropped from rows; every article is by the Research Desk and
 * says so on the article page itself. Client component only for the filter
 * state.
 */

const TOPIC_LABELS: Record<NonNullable<BlogPostMeta['topic']>, string> = {
  water: 'Water',
  food: 'Food',
  pregnancy: 'Pregnancy',
  health: 'Health',
  cosmetics: 'Cosmetics',
  home: 'Home',
  clothing: 'Clothing',
  general: 'Guide',
}

// Sort newest-first by ISO publication date so the visible date matches the
// position in the list. `modifiedISO` is intentionally NOT used here; it's
// reserved for schema (dateModified) + sitemap (lastModified). Mixing it
// into the visible sort makes posts jump to the top while showing an old
// publication date, which reads as broken to users.
const sortedPosts = [...blogPosts].sort(
  (a, b) => new Date(b.publishedISO).getTime() - new Date(a.publishedISO).getTime()
)

const TOPIC_ORDER: Array<BlogPostMeta['topic']> = [
  'water', 'food', 'home', 'health', 'pregnancy', 'cosmetics', 'clothing', 'general',
]

export interface BlogContentProps {
  /** 1-based page number of the unfiltered listing. */
  page: number
  totalPages: number
  perPage: number
}

/**
 * Pagination design, and why it is shaped this way.
 *
 * The unfiltered listing was rendering all 236 posts into a single DOM, which is
 * a heavy page and an endless scroll. It is now sliced server-side into real
 * crawlable URLs (/blog, /blog/page/2, …) so each page can carry its own
 * self-referencing canonical — Google explicitly says not to canonicalise a
 * paginated sequence back to page 1 — and stays indexable so the article links
 * on it are discoverable.
 *
 * The topic filter deliberately still searches ALL posts rather than only the
 * current page: filtering that silently ignored 200 posts would be worse than no
 * filter. Any topic bucket is small enough (<= ~60) to show in full, so when a
 * filter is active the pager is hidden and the whole bucket is listed.
 */
export default function BlogContent({ page, totalPages, perPage }: BlogContentProps) {
  const [topic, setTopic] = useState<BlogPostMeta['topic'] | 'all'>('all')

  const counts = useMemo(() => {
    const c = new Map<string, number>()
    for (const p of sortedPosts) c.set(p.topic, (c.get(p.topic) ?? 0) + 1)
    return c
  }, [])

  const isFiltered = topic !== 'all'
  const visible = isFiltered
    ? sortedPosts.filter((p) => p.topic === topic)
    : sortedPosts.slice((page - 1) * perPage, page * perPage)

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-cream py-12 sm:py-16 md:py-20">
        <div className="content">
          <div className="max-w-2xl">
            <p className="text-xs sm:text-sm font-semibold uppercase tracking-[0.2em] text-forest mb-3">
              All articles
            </p>
            <h1 className="font-display text-[clamp(1.875rem,5vw,3rem)] font-bold text-ink leading-[1.08] mb-4">
              Every microplastics article in one place
            </h1>
            <p className="text-base sm:text-lg text-gray-600">
              {sortedPosts.length} research explainers, guides, product picks,
              and how-tos. Newest first, every claim sourced.
            </p>
          </div>
        </div>
      </header>

      {/* Topic filter: quiet text links, not pills */}
      <nav
        aria-label="Filter articles by topic"
        className="content pt-8 sm:pt-10"
      >
        <ul className="flex flex-wrap gap-x-5 gap-y-2 text-sm border-b border-ink/10 pb-4">
          <li>
            <button
              onClick={() => setTopic('all')}
              aria-pressed={topic === 'all'}
              className={`inline-block py-1.5 transition-colors pressable ${
                topic === 'all'
                  ? 'font-bold text-forest underline underline-offset-4'
                  : 'text-gray-500 hover:text-ink'
              }`}
            >
              All ({sortedPosts.length})
            </button>
          </li>
          {TOPIC_ORDER.filter((t) => counts.get(t)).map((t) => (
            <li key={t}>
              <button
                onClick={() => setTopic(t)}
                aria-pressed={topic === t}
                className={`inline-block py-1.5 transition-colors pressable ${
                  topic === t
                    ? 'font-bold text-forest underline underline-offset-4'
                    : 'text-gray-500 hover:text-ink'
                }`}
              >
                {TOPIC_LABELS[t]} ({counts.get(t)})
              </button>
            </li>
          ))}
        </ul>
      </nav>

      {/* Ruled article index */}
      <div className="content pb-16 sm:pb-20 md:pb-24">
        <ul className="border-b border-ink/10">
          {visible.map((post) => (
            <li key={post.slug}>
              <Link
                href={`/blog/${post.slug}`}
                className="group grid grid-cols-1 sm:grid-cols-[6.5rem_1fr_auto] items-baseline gap-x-6 gap-y-1 border-t border-ink/10 first:border-t-0 py-5 sm:py-6"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-500">
                  {TOPIC_LABELS[post.topic] ?? 'Guide'}
                </p>
                <div className="min-w-0">
                  <h2 className="text-base sm:text-lg font-bold text-ink group-hover:text-forest transition-colors leading-snug">
                    {post.title}
                  </h2>
                  <p className="text-sm text-gray-500 leading-snug mt-1 line-clamp-2">
                    {post.excerpt}
                  </p>
                </div>
                <p className="hidden sm:flex items-center gap-2 text-xs text-gray-500 whitespace-nowrap">
                  {post.date} · {post.readTime}
                  <FaArrowRight
                    aria-hidden="true"
                    className="w-3 h-3 text-ink/25 group-hover:text-forest group-hoverable:translate-x-1 transition-[color,background-color,border-color,text-decoration-color,transform]"
                  />
                </p>
              </Link>
            </li>
          ))}
        </ul>

        {/* Real <a href> links, not a client-side "load more": crawlers need to
            follow these to reach older posts. Hidden while a topic filter is
            active, because the filter already shows that whole bucket. */}
        {!isFiltered && totalPages > 1 && (
          <nav aria-label="Article pagination" className="mt-10 flex items-center justify-between gap-4">
            {page > 1 ? (
              <Link
                href={page === 2 ? '/blog' : `/blog/page/${page - 1}`}
                rel="prev"
                className="inline-flex items-center gap-2 text-sm font-semibold text-forest underline"
              >
                &larr; Newer articles
              </Link>
            ) : (
              <span />
            )}

            <p className="text-sm text-gray-500 tabular-nums">
              Page {page} of {totalPages}
            </p>

            {page < totalPages ? (
              <Link
                href={`/blog/page/${page + 1}`}
                rel="next"
                className="inline-flex items-center gap-2 text-sm font-semibold text-forest underline"
              >
                Older articles &rarr;
              </Link>
            ) : (
              <span />
            )}
          </nav>
        )}

        {!isFiltered && totalPages > 1 && (
          <ul className="mt-6 flex flex-wrap justify-center gap-2">
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
              <li key={n}>
                <Link
                  href={n === 1 ? '/blog' : `/blog/page/${n}`}
                  aria-current={n === page ? 'page' : undefined}
                  className={
                    n === page
                      ? 'inline-flex min-w-9 justify-center rounded-lg bg-forest px-3 py-2 text-sm font-semibold text-white'
                      : 'inline-flex min-w-9 justify-center rounded-lg border border-ink/15 px-3 py-2 text-sm text-gray-700 hover:border-forest/50'
                  }
                >
                  {n}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  )
}
